// Plain-language meaning of the contract security flags produced by the
// nightly scan (see services/providers/goplus.ts).
//
// The whole value of this panel is that it says what a flag MEANS to someone
// holding the token, not just that it is true. "isMintable: true" is a feed;
// "the owner can create more tokens, diluting holders" is information.
//
// Wording lives here so the profile page, the comparison table and any future
// export all describe a flag the same way.

export type FlagSeverity = "critical" | "caution" | "good" | "neutral";

export interface FlagMeaning {
  key: string;
  /** Heading when the flag is TRUE. */
  label: string;
  /**
   * Heading when the flag is FALSE. Separate from `label` because naming the
   * risk while describing a passed check reads as a contradiction:
   * "Ownership reclaimable — ownership cannot be reclaimed."
   */
  labelWhenFalse: string;
  /** What it means when the flag is TRUE. */
  whenTrue: string;
  /** What it means when the flag is FALSE. */
  whenFalse: string;
  /** Severity when true. The false case is treated as reassuring. */
  severityWhenTrue: FlagSeverity;
  /** A few flags are reassuring when true and concerning when false. */
  invert?: boolean;
}

export const CONTRACT_FLAGS: FlagMeaning[] = [
  {
    key: "isHoneypot",
    label: "Honeypot",
    labelWhenFalse: "Sellable",
    whenTrue: "The scan could not sell this token back. You may be able to buy and not exit.",
    whenFalse: "The scan was able to simulate selling, so it is not an obvious honeypot.",
    severityWhenTrue: "critical",
  },
  {
    key: "ownerChangeBalance",
    label: "Owner can change balances",
    labelWhenFalse: "Balances are yours alone",
    whenTrue: "An owner address can alter holder balances directly, including yours.",
    whenFalse: "No function was found that lets an owner rewrite holder balances.",
    severityWhenTrue: "critical",
  },
  {
    key: "isMintable",
    label: "Mintable supply",
    labelWhenFalse: "Supply is fixed",
    whenTrue: "New tokens can be created, which dilutes existing holders.",
    whenFalse: "No mint function was found, so supply cannot simply be increased.",
    severityWhenTrue: "caution",
  },
  {
    key: "transferPausable",
    label: "Transfers can be paused",
    labelWhenFalse: "Transfers cannot be frozen",
    whenTrue: "Transfers can be frozen, which would stop you moving or selling.",
    whenFalse: "No pause function was found on transfers.",
    severityWhenTrue: "caution",
  },
  {
    key: "canTakeBackOwnership",
    label: "Ownership reclaimable",
    labelWhenFalse: "Ownership is final",
    whenTrue: "Ownership can be taken back after being renounced, so 'renounced' is not final.",
    whenFalse: "Ownership cannot be reclaimed once given up.",
    severityWhenTrue: "caution",
  },
  {
    key: "isProxy",
    label: "Upgradeable proxy",
    labelWhenFalse: "Fixed logic",
    whenTrue: "The contract's logic can be replaced later. Not inherently bad, but today's code is not a guarantee.",
    whenFalse: "The contract is not a proxy, so its logic cannot be swapped out.",
    severityWhenTrue: "neutral",
  },
  {
    key: "isOpenSource",
    label: "Verified source code",
    labelWhenFalse: "Unverified source code",
    whenTrue: "The source is published and matches the deployed bytecode, so it can be reviewed.",
    whenFalse: "The source is not verified, so nobody outside the team can read what it does.",
    severityWhenTrue: "good",
    invert: true,
  },
];

export interface ReadFlag extends FlagMeaning {
  value: boolean;
  severity: FlagSeverity;
  meaning: string;
  /** The heading to actually show, chosen from the flag's state. */
  heading: string;
}

/**
 * Turn a raw flag object into something displayable.
 *
 * Returns null when there is nothing to show. An absent scan is NOT a clean
 * bill of health, and the caller must render it as unknown rather than
 * defaulting every flag to false.
 */
export function readContractFlags(flags: Record<string, unknown> | null | undefined): ReadFlag[] | null {
  if (!flags || Object.keys(flags).length === 0) return null;

  const out: ReadFlag[] = [];
  for (const meaning of CONTRACT_FLAGS) {
    const raw = flags[meaning.key];
    if (typeof raw !== "boolean") continue; // absent flag stays absent, never false
    const concerning = meaning.invert ? !raw : raw;
    out.push({
      ...meaning,
      value: raw,
      heading: raw ? meaning.label : meaning.labelWhenFalse,
      meaning: raw ? meaning.whenTrue : meaning.whenFalse,
      severity: concerning ? (meaning.invert ? "caution" : meaning.severityWhenTrue) : "good",
    });
  }
  return out.length > 0 ? out : null;
}

/** One-line summary for a card or a comparison cell. */
export function summariseFlags(read: ReadFlag[] | null): {
  headline: string;
  worst: FlagSeverity;
  concerns: number;
} {
  if (!read) return { headline: "Not scanned", worst: "neutral", concerns: 0 };

  const concerns = read.filter((f) => f.severity === "critical" || f.severity === "caution");
  const critical = concerns.some((f) => f.severity === "critical");

  if (concerns.length === 0) {
    return { headline: "No flags raised", worst: "good", concerns: 0 };
  }
  return {
    headline: critical
      ? `${concerns.length} concern${concerns.length === 1 ? "" : "s"}, including a serious one`
      : `${concerns.length} thing${concerns.length === 1 ? "" : "s"} to know`,
    worst: critical ? "critical" : "caution",
    concerns: concerns.length,
  };
}
